import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import Select from "react-select";
import Flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import "flatpickr/dist/themes/material_green.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Header from "../components/Header";
import Footer from "../components/Footer";
import PhoneLoader from "../components/loader/PhoneLoader";
import ScrollToTop from "../components/ScrollTop";
import { useAuth } from "../provider/authProvider";
import "../Reports.css";

const TZ = "America/Lima";

const REPORT_TYPE_OPTIONS = [
    { value: "opening", label: "Apertura de tienda" },
    { value: "midday", label: "Reporte de 12:30 p.m." },
    { value: "closing", label: "Cierre de tienda" },
];

const REPORT_GUIDE = {
    opening: {
        number: "1",
        title: "Apertura de Tienda",
        time: "Al iniciar el día",
        goal: "Cuenta el efectivo real con el que abre la tienda y toma foto de la caja.",
        cashLabel: "¿Con cuánto efectivo abre la caja?",
        cashHelp: "No copies montos del sistema. Coloca solo el dinero físico contado en caja.",
        inventoryHelp: "Registra cuántos equipos físicos hay al abrir la tienda.",
        photoHelp: "Foto clara de caja al iniciar el día.",
        checklist: [
            "Cuenta el efectivo físico de caja.",
            "Cuenta todos los equipos físicos que hay en tienda.",
            "Toma la foto de caja antes de iniciar ventas.",
        ],
    },
    midday: {
        number: "2",
        title: "Reporte de 12:30pm",
        time: "Entre 12:00pm y antes de 12:30pm",
        goal: "Vuelve a contar el efectivo real y registra el movimiento de equipos hasta el medio día.",
        cashLabel: "¿Cuánto efectivo hay en caja ahora?",
        cashHelp: "Coloca el monto actual contado. No coloques un monto calculado ni esperado.",
        inventoryHelp: "Primero registra vendidos, ingresados y salidas. El sistema calculará los equipos físicos sugeridos usando la apertura.",
        photoHelp: "Foto clara de caja del reporte de 12:30pm.",
        checklist: [
            "Cuenta el efectivo físico que hay en ese momento.",
            "Registra vendidos, ingresados y salidas hasta las 12:30pm.",
            "Verifica el inventario físico sugerido y edítalo solo si el conteo real no cuadra.",
            "Toma la foto de caja del medio día.",
        ],
    },
    closing: {
        number: "3",
        title: "Cierre de Tienda",
        time: "Antes de cerrar la tienda",
        goal: "Cuenta el efectivo final, registra movimientos de equipos y confirma el inventario físico final.",
        cashLabel: "¿Con cuánto efectivo cierra la caja?",
        cashHelp: "Debe ser el dinero real contado al cierre. No ajustes el monto para que cuadre.",
        inventoryHelp: "Primero registra vendidos, ingresados y salidas desde el último reporte. El sistema calculará el inventario final sugerido.",
        photoHelp: "Foto clara de caja al cierre de tienda.",
        checklist: [
            "Cuenta el efectivo final antes de cerrar.",
            "Registra vendidos, ingresados y salidas desde el último reporte.",
            "Verifica el inventario físico final sugerido y edítalo solo si el conteo real no cuadra.",
            "Toma la foto de caja.",
        ],
    },
};

function getReportGuide(type) {
    return REPORT_GUIDE[type] || REPORT_GUIDE.opening;
}

const money = (v) => {
    const n = Number(String(v ?? "").replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
};

const moneyUI = (v) => `S/ ${Number(v || 0).toFixed(2)}`;

const intValue = (v) => {
    const n = Number(String(v ?? "").replace(/\D/g, ""));
    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
};

const intUI = (v) => String(Number(v || 0));

function formatLongDate(date) {
    const m = moment.tz(date, "YYYY-MM-DD", TZ).locale("es");
    if (!m.isValid()) return "";
    return m.format("DD [de] MMMM [del] YYYY");
}

function getReportTypeLabel(type) {
    return REPORT_TYPE_OPTIONS.find((x) => x.value === type)?.label || "Reporte";
}

function showSuccessToast(message) {
    try {
        toast.success(message, {
            position: "top-right",
            autoClose: 3000,
            closeOnClick: true,
            theme: "colored",
        });
    } catch (e) {
        console.error("Toast error:", e);
    }
}

function showWarningToast(message) {
    try {
        toast.warning(message, {
            position: "top-right",
            autoClose: 3000,
            closeOnClick: true,
            theme: "colored",
        });
    } catch (e) {
        console.error("Toast error:", e);
    }
}

function showErrorToast(message) {
    try {
        toast.error(message, {
            position: "top-right",
            autoClose: 3000,
            closeOnClick: true,
            theme: "colored",
        });
    } catch (e) {
        console.error("Toast error:", e);
    }
}

function isRealMobileDevice() {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        return false;
    }

    const userAgent = String(
        navigator.userAgent || navigator.vendor || window.opera || "",
    );
    const platform = String(navigator.platform || "");
    const maxTouchPoints = Number(navigator.maxTouchPoints || 0);

    const hasMobileUserAgent =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            userAgent,
        );

    const hasTouchHardware =
        maxTouchPoints > 0 || "ontouchstart" in window || navigator.msMaxTouchPoints > 0;

    const isDesktopPlatform =
        /Win32|Win64|Windows|MacIntel|Linux x86_64|Linux i686/i.test(platform);

    const isIPadOS = platform === "MacIntel" && maxTouchPoints > 1;

    return Boolean(
        hasMobileUserAgent &&
        hasTouchHardware &&
        (!isDesktopPlatform || isIPadOS),
    );
}

async function fileToCompressedDataUrl(file, quality = 0.8, maxSize = 1280) {
    if (!file) return null;
    const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = dataUrl;
    });

    const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);
    const ctx = canvas.getContext("2d", { alpha: false });
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", quality);
}

function CameraBox({ index, photo, onCapture, onRemove }) {
    const inputRef = useRef(null);

    return (
        <div className="reportPhotoBox">
            <button
                type="button"
                className={`reportPhotoButton ${photo ? "hasPhoto" : ""}`}
                onClick={() => inputRef.current?.click()}
            >
                {photo ? (
                    <img
                        src={photo}
                        alt={`Foto ${index}`}
                        className="reportPhotoPreview"
                    />
                ) : (
                    <span className="reportPhotoEmpty">
                        <span className="reportCameraIcon">📷</span>
                        <strong>{index === 1 ? "Caja (obligatoria)" : `Vitrina ${index - 1}${index === 2 ? " (obligatoria)" : " (opcional)"}`}</strong>
                        <small>Tocar para abrir cámara</small>
                    </span>
                )}
            </button>

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="reportHiddenInput"
                onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                        const compressed = await fileToCompressedDataUrl(file, 0.8, 1280);
                        onCapture(index - 1, compressed);
                    } catch (err) {
                        showErrorToast("No se pudo procesar la foto. Intenta nuevamente.");
                    } finally {
                        e.target.value = "";
                    }
                }}
            />

            {photo && (
                <button
                    type="button"
                    className="reportRemovePhoto"
                    onClick={() => onRemove(index - 1)}
                >
                    Quitar foto
                </button>
            )}
        </div>
    );
}

export default function Reports() {
    const { user, rol } = useAuth();
    const userRole = Number(String(rol || "0").trim());
    // Roles con acceso administrativo al módulo de reportes.
    // Rol 5 puede consultar, filtrar y auditar igual que el rol 1.
    const isManager = [1, 5, 7].includes(userRole);
    const canDeleteReports = [1, 7].includes(userRole);
    const canSeeAuditAmounts = isManager;
    const canSubmitReports = userRole === 3;
    const today = useMemo(() => moment().tz(TZ).format("YYYY-MM-DD"), []);

    const api = useMemo(
        () =>
            axios.create({
                baseURL: process.env.REACT_APP_API,
                timeout: 30000,
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                    'Content-Type': 'application/json'
                },
            }),
        [],
    );

    const calendarInputRef = useRef(null);
    const calendarInstanceRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [meta, setMeta] = useState(null);
    const [reports, setReports] = useState([]);
    const [dateStart, setDateStart] = useState(today);
    const [dateEnd, setDateEnd] = useState(today);
    const [city, setCity] = useState("");
    const [sedeId, setSedeId] = useState("0");
    const [reportType, setReportType] = useState("opening");
    const [activeListType, setActiveListType] = useState("all");
    const [cashAmount, setCashAmount] = useState("");
    const [photos, setPhotos] = useState([null, null, null, null, null]);
    const [expected, setExpected] = useState(null);
    const [inventory, setInventory] = useState({
        store: "",
        sold: "",
        incoming: "",
        outgoing: "",
    });
    const [storeManualEdit, setStoreManualEdit] = useState(false);
    const [isMobile, setIsMobile] = useState(() => isRealMobileDevice());

    const selectStyles = useMemo(
        () => ({
            control: (base, state) => ({
                ...base,
                minHeight: 46,
                borderRadius: 14,
                borderColor: state.isFocused
                    ? "rgba(77, 0, 132, 0.42)"
                    : "rgba(203, 213, 225, 0.95)",
                boxShadow: state.isFocused
                    ? "0 0 0 4px rgba(77, 0, 132, 0.10)"
                    : "0 4px 14px rgba(15, 23, 42, 0.04)",
            }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            groupHeading: (base) => ({
                ...base,
                padding: "10px 12px 8px",
                color: "#4d0084",
                backgroundColor: "#f3e8ff",
                fontSize: 12,
                fontWeight: 900,
                textTransform: "none",
            }),
            option: (base, state) => ({
                ...base,
                fontSize: 13,
                fontWeight: state.isSelected ? 800 : 600,
                color: state.isSelected ? "#4d0084" : "#1e293b",
                backgroundColor: state.isSelected
                    ? "rgba(77, 0, 132, 0.10)"
                    : state.isFocused
                        ? "rgba(77, 0, 132, 0.06)"
                        : "#fff",
            }),
        }),
        [],
    );

    const cityOptions = useMemo(
        () => [{ value: "", label: "Todas las ciudades" }, ...(meta?.cities || [])],
        [meta],
    );
    const sedeOptions = useMemo(() => {
        const base = meta?.sedes || [];
        const filtered = city
            ? base.filter((s) => String(s.city || "") === city)
            : base;
        return [
            { value: "0", label: "Todas las sedes" },
            ...filtered.map((s) => ({
                value: String(s.value),
                label: s.label,
                city: s.city,
            })),
        ];
    }, [meta, city]);

    const sedeOptionsGrouped = useMemo(() => {
        const allOption = { value: "0", label: "Todas las sedes" };
        const source = sedeOptions.filter((item) => String(item.value) !== "0");
        const groupsMap = source.reduce((acc, item) => {
            const groupName = String(item.city || "Sin ciudad").trim() || "Sin ciudad";
            if (!acc[groupName]) acc[groupName] = [];
            acc[groupName].push(item);
            return acc;
        }, {});

        const cityGroups = Object.keys(groupsMap)
            .sort((a, b) => a.localeCompare(b, "es"))
            .map((groupName) => ({
                label: groupName,
                options: groupsMap[groupName].sort((a, b) =>
                    String(a.label || "").localeCompare(String(b.label || ""), "es"),
                ),
            }));

        return [{ label: "General", options: [allOption] }, ...cityGroups];
    }, [sedeOptions]);

    const flatSedeOptions = useMemo(
        () => sedeOptionsGrouped.flatMap((group) => group.options || []),
        [sedeOptionsGrouped],
    );

    const selectedSede = useMemo(() => {
        if (!isManager) return meta?.sedes?.[0] || null;
        return (
            flatSedeOptions.find((s) => String(s.value) === String(sedeId)) ||
            flatSedeOptions[0] ||
            null
        );
    }, [isManager, meta, sedeId, flatSedeOptions]);

    const reportTitle = useMemo(
        () => `Reporte de Caja del día ${formatLongDate(today)}`,
        [today],
    );

    const currentGuide = useMemo(() => getReportGuide(reportType), [reportType]);

    const visibleInventoryFields = useMemo(() => {
        if (reportType === "opening") {
            return ["store"];
        }

        return ["sold", "incoming", "outgoing", "store"];
    }, [reportType]);

    const showField = (field) => visibleInventoryFields.includes(field);

    const sortedTodayReports = useMemo(() => {
        return [...(reports || [])]
            .filter((item) => String(item.reportDate || today) === String(today))
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }, [reports, today]);

    const inventoryBaseReport = useMemo(() => {
        if (reportType === "midday") {
            return sortedTodayReports.find((item) => item.reportType === "opening") || null;
        }

        if (reportType === "closing") {
            return (
                sortedTodayReports.find((item) => item.reportType === "midday") ||
                sortedTodayReports.find((item) => item.reportType === "opening") ||
                null
            );
        }

        return null;
    }, [reportType, sortedTodayReports]);

    const inventoryBaseCount = useMemo(() => {
        return inventoryBaseReport ? intValue(inventoryBaseReport.equipmentStoreCount) : null;
    }, [inventoryBaseReport]);

    const inventorySuggestedCount = useMemo(() => {
        if (reportType === "opening") return intValue(inventory.store);
        if (inventoryBaseCount === null) return intValue(inventory.store);

        const sold = intValue(inventory.sold);
        const incoming = intValue(inventory.incoming);
        const outgoing = intValue(inventory.outgoing);

        return Math.max(0, inventoryBaseCount + incoming - sold - outgoing);
    }, [inventory, inventoryBaseCount, reportType]);

    useEffect(() => {
        if (reportType === "opening") return;
        if (storeManualEdit) return;
        if (inventoryBaseCount === null) return;

        const nextStore = String(inventorySuggestedCount);
        setInventory((prev) =>
            String(prev.store) === nextStore ? prev : { ...prev, store: nextStore },
        );
    }, [inventorySuggestedCount, inventoryBaseCount, reportType, storeManualEdit]);

    const storeFieldLabel = useMemo(() => {
        if (reportType === "opening") return "Equipos físicos en tienda";
        if (reportType === "midday") return "Equipos físicos en tienda a las 12:30pm";
        return "Inventario físico final en tienda";
    }, [reportType]);

    const storeFieldHelp = useMemo(() => {
        if (reportType === "opening") {
            return "Cuenta los equipos reales que tienes en tienda al iniciar el día.";
        }

        if (inventoryBaseCount === null) {
            return "No se encontró un reporte anterior para calcular automáticamente. Puedes habilitar la edición y colocar el conteo físico real.";
        }

        if (storeManualEdit) {
            return "Edición manual habilitada. Usa esta opción solo si el conteo físico real no coincide con el cálculo del sistema.";
        }

        return `Calculado automáticamente desde ${getReportTypeLabel(inventoryBaseReport?.reportType)}: ${inventoryBaseCount} + ingresados - vendidos - salidas.`;
    }, [inventoryBaseCount, inventoryBaseReport, reportType, storeManualEdit]);

    async function fetchMeta() {
        const res = await api.get("/reports/meta");
        const data = res?.data || {};
        setMeta(data);
        if (!isManager && data?.user?.sedeId) setSedeId(String(data.user.sedeId));
    }

    async function fetchReports(overrides = {}) {
        setLoading(true);
        try {
            const params = {
                start: overrides.start ?? dateStart,
                end: overrides.end ?? dateEnd,
                city: overrides.city ?? city,
                sede_id: overrides.sede_id ?? sedeId,
            };
            const res = await api.get("/reports/list", { params });
            setReports(res?.data?.reports || []);
        } catch (error) {
            showErrorToast(
                error?.response?.data?.error || "No se pudieron cargar los reportes.",
            );
        } finally {
            setLoading(false);
        }
    }

    async function fetchExpected(overrides = {}) {
        const currentSede = !isManager
            ? meta?.user?.sedeId
            : (overrides.sede_id ?? sedeId);
        if (!currentSede || String(currentSede) === "0") {
            setExpected(null);
            return;
        }
        try {
            const res = await api.get("/reports/expected", {
                params: {
                    report_date: today,
                    report_type: overrides.report_type ?? reportType,
                    sede_id: currentSede,
                },
            });
            setExpected(res?.data || null);
        } catch (error) {
            setExpected(null);
        }
    }

    useEffect(() => {
        const validateDevice = () => {
            const nextIsMobile = isRealMobileDevice();
            setIsMobile(nextIsMobile);

            if (!nextIsMobile) {
                setPhotos([null, null, null, null, null]);
            }
        };

        validateDevice();

        if (typeof window !== "undefined") {
            window.addEventListener("resize", validateDevice);
            window.addEventListener("orientationchange", validateDevice);
            window.addEventListener("focus", validateDevice);
        }

        return () => {
            if (typeof window !== "undefined") {
                window.removeEventListener("resize", validateDevice);
                window.removeEventListener("orientationchange", validateDevice);
                window.removeEventListener("focus", validateDevice);
            }
        };
    }, []);

    useEffect(() => {
        fetchMeta()
            .then(() => fetchReports())
            .catch(() => showErrorToast("No se pudo cargar la información inicial."));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!calendarInputRef.current) return undefined;
        if (calendarInstanceRef.current) calendarInstanceRef.current.destroy();

        calendarInstanceRef.current = Flatpickr(calendarInputRef.current, {
            mode: "range",
            dateFormat: "Y-m-d",
            defaultDate: [dateStart, dateEnd],
            locale: Spanish,
            maxDate: today,
            disableMobile: true,
            onClose: async (selectedDates) => {
                if (!selectedDates?.length) return;
                const start = moment(selectedDates[0]).tz(TZ).format("YYYY-MM-DD");
                const end = moment(
                    selectedDates[selectedDates.length - 1] || selectedDates[0],
                )
                    .tz(TZ)
                    .format("YYYY-MM-DD");
                setDateStart(start);
                setDateEnd(end);
                await fetchReports({ start, end });
            },
        });

        return () => calendarInstanceRef.current?.destroy();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [today]);

    useEffect(() => {
        if (meta) fetchExpected();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta, reportType, sedeId]);

    const onChangePhoto = (idx, value) => {
        setPhotos((prev) => prev.map((p, i) => (i === idx ? value : p)));
    };

    const onRemovePhoto = (idx) => {
        setPhotos((prev) => prev.map((p, i) => (i === idx ? null : p)));
    };

    const updateInventory = (field, value) => {
        const nextValue = String(value || "").replace(/\D/g, "").slice(0, 5);
        setInventory((prev) => ({ ...prev, [field]: nextValue }));
    };

    const inventoryPayload = useMemo(() => {
        const store = intValue(inventory.store);
        const sold = reportType === "opening" ? 0 : intValue(inventory.sold);
        const incoming = reportType === "opening" ? 0 : intValue(inventory.incoming);
        const outgoing = reportType === "opening" ? 0 : intValue(inventory.outgoing);

        return {
            equipment_store_count: store,
            equipment_sold_count: sold,
            equipment_in_count: incoming,
            equipment_out_count: outgoing,
            closing_store_count: reportType === "closing" ? store : null,
        };
    }, [inventory, reportType]);

    const handleReportTypeChange = (nextType) => {
        const cleanType = nextType || "opening";
        setReportType(cleanType);
        setStoreManualEdit(false);

        if (cleanType === "opening") {
            setInventory((prev) => ({
                ...prev,
                sold: "",
                incoming: "",
                outgoing: "",
            }));
        } else {
            setInventory((prev) => ({
                ...prev,
                store: "",
            }));
        }
    };

    const onDeleteReport = async (reportId) => {
        if (!canDeleteReports || !reportId) return;

        const confirmed = window.confirm(
            "¿Seguro que deseas eliminar este reporte? Esta acción lo ocultará del listado."
        );

        if (!confirmed) return;

        setDeletingId(reportId);
        try {
            await api.delete(`/reports/${reportId}`);
            showSuccessToast("Reporte eliminado correctamente.");
            await fetchReports({ start: dateStart, end: dateEnd });
        } catch (error) {
            showErrorToast(
                error?.response?.data?.error || "No se pudo eliminar el reporte.",
            );
        } finally {
            setDeletingId(null);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const validPhotos = photos.filter(Boolean);
        if (photos.some((photo, index) => photo && index > 0 && !photos[index - 1])) {
            return showErrorToast("Carga las vitrinas en orden, sin dejar espacios vacíos.");
        }
        const amount = money(cashAmount);

        if (!reportType) return showErrorToast("Selecciona el tipo de reporte.");
        if (cashAmount === "" || amount < 0)
            return showErrorToast("Ingresa el monto actual de caja en efectivo.");

        const hasInventoryError = Object.values(inventoryPayload)
            .filter((value) => value !== null)
            .some((value) => !Number.isFinite(Number(value)) || Number(value) < 0);

        if (hasInventoryError) {
            return showErrorToast("Revisa las cantidades de equipos. Solo se permiten números positivos.");
        }

        if (!isMobile)
            return showErrorToast("Para enviar el reporte con foto debes ingresar desde un celular.");
        if (!photos[0] || !photos[1])
            return showErrorToast("Debes tomar una foto de caja y al menos una foto de equipos.");
        if (validPhotos.length > 5)
            return showErrorToast("Máximo una foto de caja y cuatro fotos de equipos.");

        setSaving(true);
        try {
            const res = await api.post("/reports/add", {
                report_date: today,
                report_type: reportType,
                reported_cash_amount: amount,
                ...inventoryPayload,
                photos: validPhotos,
            });
            const balanced = Number(res?.data?.isBalanced) === 1;
            if (!canSeeAuditAmounts) {
                showSuccessToast("Reporte guardado correctamente. Gracias por colocar el monto real contado en caja.");
            } else if (balanced) {
                showSuccessToast("Reporte guardado correctamente. La caja cuadra.");
            } else {
                showWarningToast("Reporte guardado, pero la caja no cuadra.");
            }
            setCashAmount("");
            setInventory({
                store: "",
                sold: "",
                incoming: "",
                outgoing: "",
            });
            setPhotos([null, null, null, null, null]);
            setStoreManualEdit(false);
            await fetchExpected();
            await fetchReports({ start: dateStart, end: dateEnd });
        } catch (error) {
            showErrorToast(
                error?.response?.data?.error || "No se pudo guardar el reporte.",
            );
        } finally {
            setSaving(false);
        }
    };

    const currentDifference =
        expected && cashAmount !== ""
            ? money(cashAmount) - money(expected.expectedCash)
            : 0;
    const currentIsBalanced =
        expected && cashAmount !== "" ? Math.abs(currentDifference) <= 1 : null;

    const reportCounts = useMemo(() => {
        const opening = reports.filter((item) => item.reportType === "opening").length;
        const midday = reports.filter((item) => item.reportType === "midday").length;
        const closing = reports.filter((item) => item.reportType === "closing").length;
        return {
            all: reports.length,
            opening,
            midday,
            closing,
        };
    }, [reports]);

    const reportListTabs = useMemo(
        () => [
            { value: "all", label: "Todos", count: reportCounts.all },
            { value: "opening", label: "Apertura", count: reportCounts.opening },
            { value: "midday", label: "12:30 p.m.", count: reportCounts.midday },
            { value: "closing", label: "Cierre", count: reportCounts.closing },
        ],
        [reportCounts],
    );

    const visibleReports = useMemo(
        () =>
            activeListType === "all"
                ? reports
                : reports.filter((item) => item.reportType === activeListType),
        [activeListType, reports],
    );

    const storePreview = inventoryPayload.equipment_store_count;

    return (
        <>
            <div>
                <Header />
                <ScrollToTop />
                <section className="sectionBody sectionBodyReports">
                    <div className="containerCustom containerBody">
                        <div className="blockBody">
                            <div className="reportsPage">
                                <section className="reportsHero">
                                    <div>
                                        <span className="reportsEyebrow">Reportes diarios</span>
                                        <h1>{reportTitle}</h1>
                                        <p>
                                            Ciudad:{" "}
                                            <strong>
                                                {selectedSede?.city || meta?.user?.city || "—"}
                                            </strong>{" "}
                                            · Sede:{" "}
                                            <strong>
                                                {selectedSede?.label || meta?.user?.sedeName || "—"}
                                            </strong>
                                        </p>
                                    </div>
                                    <div className="reportsHeroCard">
                                        <span>Asesor</span>
                                        <strong>
                                            {meta?.user?.advisorName ||
                                                user?.userName ||
                                                user?.name ||
                                                "—"}
                                        </strong>
                                    </div>
                                </section>

                                <section className={`reportsGrid ${canSubmitReports ? "" : "reportsGridOnlyList"}`}>
                                    {canSubmitReports && (
                                        <form className="reportFormCard" onSubmit={onSubmit}>
                                            <div className="reportCardHeader reportCardHeaderStack">
                                                <div>
                                                    <h2>Enviar reporte diario</h2>
                                                    <p>El asesor solo debe contar y reportar lo real: efectivo físico, equipos y fotos.</p>
                                                </div>
                                            </div>

                                            <div className="reportStepsGuide" aria-label="Guía de reportes diarios">
                                                {REPORT_TYPE_OPTIONS.map((option, idx) => {
                                                    const guide = getReportGuide(option.value);
                                                    const active = reportType === option.value;
                                                    return (
                                                        <button
                                                            key={option.value}
                                                            type="button"
                                                            className={`reportStepPill ${active ? "active" : ""}`}
                                                            onClick={() => handleReportTypeChange(option.value)}
                                                        >
                                                            <strong>{idx + 1}</strong>
                                                            <span>{guide.title}</span>
                                                            <small>{guide.time}</small>
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <label className="reportField reportTypeSelectField">
                                                <span>Tipo de reporte seleccionado</span>
                                                <Select
                                                    options={REPORT_TYPE_OPTIONS}
                                                    value={REPORT_TYPE_OPTIONS.find(
                                                        (x) => x.value === reportType,
                                                    )}
                                                    onChange={(opt) =>
                                                        handleReportTypeChange(opt?.value || "opening")
                                                    }
                                                    styles={selectStyles}
                                                    menuPortalTarget={document.body}
                                                />
                                            </label>

                                            <div className="reportCurrentGuide">
                                                <span>Paso {currentGuide.number}</span>
                                                <strong>{currentGuide.title}</strong>
                                                <p>{currentGuide.goal}</p>
                                            </div>

                                            <div className="reportChecklist">
                                                <strong>Antes de guardar, verifica:</strong>
                                                {currentGuide.checklist.map((item) => (
                                                    <span key={item}>✓ {item}</span>
                                                ))}
                                            </div>

                                            <label className="reportField reportCashField">
                                                <span>{currentGuide.cashLabel}</span>
                                                <small>{currentGuide.cashHelp}</small>
                                                <div className="reportMoneyInputWrap">
                                                    <strong>S/</strong>
                                                    <input
                                                        value={cashAmount}
                                                        onChange={(e) => {
                                                            const value = e.target.value
                                                                .replace(/[^0-9.]/g, "")
                                                                .replace(/(\..*)\./g, "$1");
                                                            setCashAmount(value);
                                                        }}
                                                        inputMode="decimal"
                                                        placeholder="0.00"
                                                    />
                                                </div>
                                            </label>

                                            {canSeeAuditAmounts ? (
                                                <div className="reportExpectedBox">
                                                    <div>
                                                        <span>Caja anterior</span>
                                                        <strong>{moneyUI(expected?.previousCash || 0)}</strong>
                                                    </div>
                                                    <div>
                                                        <span>Movimiento efectivo hoy</span>
                                                        <strong>
                                                            {moneyUI(expected?.todayCashMovement || 0)}
                                                        </strong>
                                                    </div>
                                                    <div>
                                                        <span>Caja esperada</span>
                                                        <strong>{moneyUI(expected?.expectedCash || 0)}</strong>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="reportHonestyBox">
                                                    <strong>Importante</strong>
                                                    <span>Cuenta el efectivo físico y escribe el monto real. El sistema no muestra la caja esperada para evitar ajustes manuales.</span>
                                                </div>
                                            )}

                                            <div className="reportInventoryBox">
                                                <div className="reportInventoryHeader">
                                                    <strong>Control de equipos</strong>
                                                    <span>
                                                        {currentGuide.inventoryHelp}
                                                    </span>
                                                </div>

                                                <div className="reportInventoryGrid">
                                                    {showField("sold") && (
                                                        <label className="reportField reportInventoryField">
                                                            <span>Equipos vendidos</span>
                                                            <small>{reportType === "closing" ? "Vendidos desde el último reporte." : "Vendidos hasta las 12:30pm."}</small>
                                                            <div className="reportNumberInputWrap">
                                                                <strong>#</strong>
                                                                <input
                                                                    value={inventory.sold}
                                                                    onChange={(e) => updateInventory("sold", e.target.value)}
                                                                    inputMode="numeric"
                                                                    placeholder="0"
                                                                />
                                                            </div>
                                                        </label>
                                                    )}

                                                    {showField("incoming") && (
                                                        <label className="reportField reportInventoryField">
                                                            <span>Equipos ingresados</span>
                                                            <small>{reportType === "closing" ? "Ingresados desde el último reporte." : "Ingresados hasta las 12:30pm."}</small>
                                                            <div className="reportNumberInputWrap">
                                                                <strong>#</strong>
                                                                <input
                                                                    value={inventory.incoming}
                                                                    onChange={(e) => updateInventory("incoming", e.target.value)}
                                                                    inputMode="numeric"
                                                                    placeholder="0"
                                                                />
                                                            </div>
                                                        </label>
                                                    )}

                                                    {showField("outgoing") && (
                                                        <label className="reportField reportInventoryField">
                                                            <span>Salida de equipos</span>
                                                            <small>Traslados, garantías u otra salida sin venta.</small>
                                                            <div className="reportNumberInputWrap">
                                                                <strong>#</strong>
                                                                <input
                                                                    value={inventory.outgoing}
                                                                    onChange={(e) => updateInventory("outgoing", e.target.value)}
                                                                    inputMode="numeric"
                                                                    placeholder="0"
                                                                />
                                                            </div>
                                                        </label>
                                                    )}

                                                    {showField("store") && (
                                                        <label className={`reportField reportInventoryField reportInventoryFieldFull ${reportType !== "opening" ? "reportAutoInventoryField" : ""}`}>
                                                            <span>{storeFieldLabel}</span>
                                                            <small>{storeFieldHelp}</small>

                                                            {reportType !== "opening" && (
                                                                <div className="reportAutoInventoryBar">
                                                                    <div>
                                                                        <span>Conteo sugerido</span>
                                                                        <strong>{intUI(inventorySuggestedCount)}</strong>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className={storeManualEdit ? "reportAutoEditBtn active" : "reportAutoEditBtn"}
                                                                        onClick={() => {
                                                                            setStoreManualEdit((prev) => !prev);
                                                                            if (storeManualEdit && inventoryBaseCount !== null) {
                                                                                setInventory((prev) => ({
                                                                                    ...prev,
                                                                                    store: String(inventorySuggestedCount),
                                                                                }));
                                                                            }
                                                                        }}
                                                                    >
                                                                        {storeManualEdit ? "Usar cálculo automático" : "Editar conteo"}
                                                                    </button>
                                                                </div>
                                                            )}

                                                            <div className={`reportNumberInputWrap ${reportType !== "opening" && !storeManualEdit ? "isDisabled" : ""}`}>
                                                                <strong>#</strong>
                                                                <input
                                                                    value={inventory.store}
                                                                    onChange={(e) => updateInventory("store", e.target.value)}
                                                                    inputMode="numeric"
                                                                    placeholder="0"
                                                                    disabled={reportType !== "opening" && !storeManualEdit}
                                                                />
                                                            </div>
                                                        </label>
                                                    )}
                                                </div>

                                                {reportType === "closing" && (
                                                    <div className="reportClosingInventory">
                                                        <span>Inventario físico final confirmado</span>
                                                        <strong>{intUI(storePreview)}</strong>
                                                    </div>
                                                )}

                                                {canSeeAuditAmounts ? (
                                                    <div className="reportWithdrawalBox">
                                                        <span>Retiro de caja registrado en el sistema</span>
                                                        <strong>{moneyUI(expected?.cashWithdrawalAmount || 0)}</strong>
                                                    </div>
                                                ) : (
                                                    <div className="reportOperationalNote">
                                                        <strong>Recuerda:</strong> si hubo retiro de caja, reporta solo el efectivo que quedó físicamente en tienda.
                                                    </div>
                                                )}
                                            </div>

                                            {canSeeAuditAmounts && currentIsBalanced !== null && (
                                                <div
                                                    className={`reportBalancePreview ${currentIsBalanced ? "ok" : "bad"}`}
                                                >
                                                    {currentIsBalanced
                                                        ? "La caja está cuadrando dentro de la tolerancia de ± S/ 1.00."
                                                        : `Diferencia detectada: ${moneyUI(currentDifference)}`}
                                                </div>
                                            )}

                                            {isMobile && (
                                                <div className="reportPhotosSection reportPhotoRedesign">
                                                    <div className="reportPhotoHeader">
                                                        <div><strong>Reporte fotográfico de tienda</strong></div>
                                                    </div>
                                                    <div className="reportPhotoGroup">
                                                        <h4>1. Fotografía de dinero en caja</h4>
                                                        <p>{currentGuide.photoHelp} · Una fotografía obligatoria</p>
                                                        <div className="reportCashPhoto"><CameraBox index={1} photo={photos[0]} onCapture={onChangePhoto} onRemove={onRemovePhoto} /></div>
                                                    </div>
                                                    <br/>
                                                    <div className="reportPhotoGroup">
                                                        <h4>2. Fotografías de equipos en tienda</h4>
                                                        <p>Sube una fotografía general o hasta cuatro, según la distribución de las vitrinas.</p>
                                                        <div className="reportVitrinaGrid">
                                                            {[2, 3, 4, 5].map((index) => (
                                                                <>
                                                                <CameraBox key={index} index={index} photo={photos[index - 1]} onCapture={onChangePhoto} onRemove={onRemovePhoto} />
                                                                <br/>
                                                                </>
                                                            ))}
                                                        </div>
                                                        <div className="reportPhotoCounter"><span>{photos.slice(1).filter(Boolean).length} de 4 fotografías de equipos</span><span>Máximo: 4</span></div>
                                                    </div>
                                                    <div className="reportPhotoSummary">
                                                        
                                                    </div>
                                                </div>
                                            )}

                                            <button
                                                className="reportSubmitBtn"
                                                type="submit"
                                                disabled={saving}
                                            >
                                                {saving
                                                    ? "Guardando reporte..."
                                                    : "Guardar reporte de caja"}
                                            </button>
                                        </form>
                                    )}

                                    <section className="reportsListCard">
                                        <div className="reportCardHeader reportsListHeader">
                                            <div>
                                                <h2>Reportes registrados</h2>
                                                <p>{canSeeAuditAmounts ? "Verde: caja correcta · Rojo: caja con diferencia." : "Revisa tus reportes enviados y las fotos registradas."}</p>
                                            </div>
                                        </div>

                                        <div className="reportsFilters">
                                            <input
                                                ref={calendarInputRef}
                                                className="reportsDateInput"
                                                readOnly
                                                value={
                                                    dateStart === dateEnd
                                                        ? dateStart
                                                        : `${dateStart} a ${dateEnd}`
                                                }
                                            />
                                            {isManager && (
                                                <>
                                                    <Select
                                                        options={cityOptions}
                                                        value={
                                                            cityOptions.find((x) => x.value === city) ||
                                                            cityOptions[0]
                                                        }
                                                        onChange={async (opt) => {
                                                            const nextCity = opt?.value || "";
                                                            setCity(nextCity);
                                                            setSedeId("0");
                                                            await fetchReports({
                                                                city: nextCity,
                                                                sede_id: "0",
                                                            });
                                                        }}
                                                        styles={selectStyles}
                                                        menuPortalTarget={document.body}
                                                    />
                                                    <Select
                                                        options={sedeOptionsGrouped}
                                                        value={
                                                            flatSedeOptions.find(
                                                                (x) => String(x.value) === String(sedeId),
                                                            ) || flatSedeOptions[0]
                                                        }
                                                        onChange={async (opt) => {
                                                            const nextSede = String(opt?.value || "0");
                                                            setSedeId(nextSede);
                                                            await fetchReports({ sede_id: nextSede });
                                                        }}
                                                        styles={selectStyles}
                                                        menuPortalTarget={document.body}
                                                    />
                                                </>
                                            )}
                                        </div>

                                        <div className="reportTypeTabs" role="tablist" aria-label="Tipos de reporte">
                                            {reportListTabs.map((tab) => (
                                                <button
                                                    key={tab.value}
                                                    type="button"
                                                    className={`reportTypeTab ${activeListType === tab.value ? "active" : ""}`}
                                                    onClick={() => setActiveListType(tab.value)}
                                                >
                                                    <span>{tab.label}</span>
                                                    <strong>{tab.count}</strong>
                                                </button>
                                            ))}
                                        </div>

                                        <div className="reportTypeCounters">
                                            <div>
                                                <span>Apertura de Tienda</span>
                                                <strong>{reportCounts.opening}</strong>
                                            </div>
                                            <div>
                                                <span>Reporte de 12:30 p.m.</span>
                                                <strong>{reportCounts.midday}</strong>
                                            </div>
                                            <div>
                                                <span>Cierre de Tienda</span>
                                                <strong>{reportCounts.closing}</strong>
                                            </div>
                                        </div>

                                        {loading ? (
                                            <div className="reportsLoader">
                                                <PhoneLoader />
                                            </div>
                                        ) : visibleReports.length === 0 ? (
                                            <div className="reportsEmpty">
                                                No hay reportes registrados en la fecha seleccionada.
                                            </div>
                                        ) : (
                                            <div className="reportsList">
                                                {visibleReports.map((r) => {
                                                    const ok = Number(r.isBalanced) === 1;
                                                    return (
                                                        <article
                                                            key={r.id}
                                                            className={`reportItem ${canSeeAuditAmounts ? (ok ? "ok" : "bad") : "advisor"}`}
                                                        >
                                                            <div className="reportItemTop">
                                                                <div>
                                                                    <strong>
                                                                        {getReportTypeLabel(r.reportType)}
                                                                    </strong>
                                                                    <span>
                                                                        {moment(r.createdAt)
                                                                            .tz(TZ)
                                                                            .format("DD/MM/YYYY hh:mm A")}
                                                                    </span>
                                                                </div>
                                                                <div className="reportItemActions">
                                                                    {canSeeAuditAmounts && <b>{ok ? "Cuadra" : "No cuadra"}</b>}
                                                                    {canDeleteReports && (
                                                                        <button
                                                                            type="button"
                                                                            className="reportDeleteBtn"
                                                                            onClick={() => onDeleteReport(r.id)}
                                                                            disabled={deletingId === r.id}
                                                                        >
                                                                            {deletingId === r.id ? "Eliminando..." : "Eliminar"}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="reportItemMeta">
                                                                <span>{r.city || "—"}</span>
                                                                <span>{r.sedeName || "—"}</span>
                                                                <span>{r.advisorName || "—"}</span>
                                                            </div>

                                                            <div className={`reportAmounts ${canSeeAuditAmounts ? "" : "advisorAmounts"}`}>
                                                                <div>
                                                                    <span>Monto real reportado</span>
                                                                    <strong>
                                                                        {moneyUI(r.reportedCashAmount)}
                                                                    </strong>
                                                                </div>
                                                                {canSeeAuditAmounts && (
                                                                    <>
                                                                        <div>
                                                                            <span>Caja esperada</span>
                                                                            <strong>
                                                                                {moneyUI(r.expectedCashAmount)}
                                                                            </strong>
                                                                        </div>
                                                                        <div>
                                                                            <span>Diferencia</span>
                                                                            <strong>{moneyUI(r.cashDifference)}</strong>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>

                                                            <div className="reportInventorySummary">
                                                                <div>
                                                                    <span>En tienda</span>
                                                                    <strong>{intUI(r.equipmentStoreCount)}</strong>
                                                                </div>
                                                                <div>
                                                                    <span>Vendidos</span>
                                                                    <strong>{intUI(r.equipmentSoldCount)}</strong>
                                                                </div>
                                                                <div>
                                                                    <span>Ingresados</span>
                                                                    <strong>{intUI(r.equipmentInCount)}</strong>
                                                                </div>
                                                                <div>
                                                                    <span>Salida equipos</span>
                                                                    <strong>{intUI(r.equipmentOutCount)}</strong>
                                                                </div>
                                                                {r.reportType === "closing" && (
                                                                    <div>
                                                                        <span>Inventario final</span>
                                                                        <strong>{intUI(r.closingStoreCount || r.equipmentStoreCount)}</strong>
                                                                    </div>
                                                                )}
                                                                {canSeeAuditAmounts && (
                                                                    <div>
                                                                        <span>Retiro caja</span>
                                                                        <strong>{moneyUI(r.cashWithdrawalAmount)}</strong>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="reportThumbs">
                                                                {r.photo1 && (
                                                                    <a
                                                                        href={process.env.REACT_APP_API + r.photo1}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        <img src={process.env.REACT_APP_API + r.photo1} alt="Foto de Caja" />
                                                                    </a>
                                                                )}
                                                                {r.photo2 && (
                                                                    <a
                                                                        href={process.env.REACT_APP_API + r.photo2}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        <img src={process.env.REACT_APP_API + r.photo2} alt="Foto de Equipos" />
                                                                    </a>
                                                                )}
                                                                {[3, 4, 5].map((index) => {
                                                                    const url = r[`photo${index}`];
                                                                    return url ? (
                                                                        <a key={index} href={process.env.REACT_APP_API + url} target="_blank" rel="noreferrer" title={`Vitrina ${index - 1}`}>
                                                                            <img src={process.env.REACT_APP_API + url} alt={`Vitrina ${index - 1}`} />
                                                                        </a>
                                                                    ) : null;
                                                                })}
                                                            </div>
                                                        </article>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </section>
                                </section>
                            </div>
                        </div>
                    </div>
                </section>
                <Footer />
                <ToastContainer />
            </div>
        </>
    );
}
